import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const [mode, setMode] = useState(resetToken ? 'reset' : 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [appSettings, setAppSettings] = useState({
    logo_url: '/api/uploads/app/logo_goloc.jpg',
    login_background_url: null,
    login_background_color: '#1e3a5f',
    app_title: 'GOLOC',
    app_subtitle: 'Sistema de Gestão de Locadora de Veículos'
  });
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    newPassword: ''
  });

  useEffect(() => {
    loadAppSettings();
  }, []);

  const loadAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/app`);
      if (response.data) {
        setAppSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Use defaults
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      }, { withCredentials: true });
      
      if (rememberMe) {
        localStorage.setItem('token', response.data.token);
      }
      
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard', { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name
      }, { withCredentials: true });
      
      localStorage.setItem('token', response.data.token);
      toast.success('Conta criada com sucesso!');
      navigate('/dashboard', { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
      toast.success('Se o e-mail existir, você receberá instruções de recuperação');
      setMode('login');
    } catch (error) {
      toast.error('Erro ao processar solicitação');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: formData.newPassword
      });
      toast.success('Senha alterada com sucesso!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Determine background style
  const backgroundStyle = appSettings.login_background_url 
    ? {
        backgroundImage: `url(${BACKEND_URL}${appSettings.login_background_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #1e3a5f 100%)'
      };

  return (
    <div 
      className="min-h-screen relative flex items-center justify-end p-4 lg:p-12" 
      data-testid="login-page"
      style={backgroundStyle}
    >
      {/* Dark overlay for better readability */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to right, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)'
        }}
      />

      {/* Logo and Title - Bottom Left */}
      <div className="absolute bottom-8 left-8 z-10 hidden lg:block">
        <div className="flex items-center gap-4">
          {appSettings.logo_url && (
            <img 
              src={`${BACKEND_URL}${appSettings.logo_url}`}
              alt={appSettings.app_title || 'GOLOC'}
              className="w-16 h-16 rounded-xl shadow-lg object-contain bg-white p-2"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${BACKEND_URL}/api/uploads/app/logo_goloc.jpg`;
              }}
            />
          )}
          <div>
            <h2 className="text-2xl font-bold text-white drop-shadow-lg">{appSettings.app_title || 'GOLOC'}</h2>
            <p className="text-white/80 text-sm drop-shadow">{appSettings.app_subtitle || 'Gestão de Locação Inteligente'}</p>
          </div>
        </div>
      </div>

      {/* Floating Login Card - Right Side */}
      <div className="relative z-10 w-full max-w-md">
        {/* Mobile Logo */}
        <div className="lg:hidden text-center mb-6">
          <div className="inline-flex items-center gap-3 bg-white/90 backdrop-blur-sm px-4 py-3 rounded-xl">
            {appSettings.logo_url && (
              <img 
                src={`${BACKEND_URL}${appSettings.logo_url}`}
                alt={appSettings.app_title || 'GOLOC'}
                className="w-10 h-10 rounded-lg object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `${BACKEND_URL}/api/uploads/app/logo_goloc.jpg`;
                }}
              />
            )}
            <span className="text-xl font-bold text-gray-800">{appSettings.app_title || 'GOLOC'}</span>
          </div>
        </div>

        {/* White Form Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === 'login' && 'Bem-vindo!'}
              {mode === 'register' && 'Criar Conta'}
              {mode === 'forgot' && 'Recuperar Senha'}
              {mode === 'reset' && 'Nova Senha'}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {mode === 'login' && 'Faça login para acessar o sistema'}
              {mode === 'register' && 'Preencha os dados para criar sua conta'}
              {mode === 'forgot' && 'Informe seu e-mail para recuperar a senha'}
              {mode === 'reset' && 'Defina sua nova senha'}
            </p>
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email" className="text-gray-700 text-sm">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-11 border-gray-300"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="password" className="text-gray-700 text-sm">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-11 border-gray-300"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="remember" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                  />
                  <Label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer">
                    Lembrar-me
                  </Label>
                </div>
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Esqueceu a senha?
                </button>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 text-sm font-semibold bg-[#1e3a5f] hover:bg-[#2a4a70]" 
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">ou</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-11 text-sm font-medium"
                onClick={handleGoogleLogin}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Entrar com Google
              </Button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email" className="text-gray-700 text-sm">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-11 border-gray-300"
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-11 text-sm font-semibold bg-[#1e3a5f] hover:bg-[#2a4a70]" 
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar instruções'}
              </Button>
              
              <button
                type="button"
                onClick={() => setMode('login')}
                className="w-full text-sm text-gray-600 hover:text-gray-800"
              >
                Voltar para login
              </button>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="newPassword" className="text-gray-700 text-sm">Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="pl-10 h-11 border-gray-300"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirmPassword" className="text-gray-700 text-sm">Confirmar Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 h-11 border-gray-300"
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-11 text-sm font-semibold bg-[#1e3a5f] hover:bg-[#2a4a70]" 
                disabled={loading}
              >
                {loading ? 'Redefinindo...' : 'Redefinir senha'}
              </Button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-white/70 mt-6 drop-shadow">
          © 2026 {appSettings.app_title || 'GOLOC'}. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
